import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { updatePipedriveInfo } from "../api/pipedrive.api";

const usePipedriveIntegrations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const updatePipeDriveInfo = async (payload: {
    companyId?: number;
    userId?: number;
    pipeOrganizationId?: number;
    pipePersonId?: number;
  }) => {
    const result = await updatePipeDriveInfoMutation.mutateAsync(payload);
    return result;
  };

  const updatePipeDriveInfoMutation = useMutation({
    mutationFn: (payload: {
      companyId?: number;
      userId?: number;
      pipeOrganizationId?: number;
      pipePersonId?: number;
    }) => updatePipedriveInfo(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update company info in Pipedrive:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-pipedrive-company-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-pipedrive-company-updated"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["adminCompanies"] });
    },
  });

  const isUpdatingPipeDriveInfo = updatePipeDriveInfoMutation.isPending;

  return {
    updatePipeDriveInfo,
    isUpdatingPipeDriveInfo,
  };
};

export default usePipedriveIntegrations;
